<template>
  <component
    :is="linkComponent"
    :to="computedUrl"
    :href="computedUrl"
    :target="item.openInNewTab ? '_blank' : '_self'"
    :rel="item.openInNewTab ? 'noopener noreferrer' : ''"
    class="dropdown-item"
    :class="customClasses"
    @click="$emit('close')"
  >
    <i v-if="item.icon" :class="item.icon + ' me-2'"></i>
    {{ displayLabel }}
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { MenuItemData } from '@/components/admin/MenuBuilder.vue'
import {
  resolveMenuItemLabel,
  resolveMenuItemUrl,
  type MenuLookupPage,
} from '@/utils/menuBuilderHelpers'

interface Props {
  item: MenuItemData
  isLoggedIn: boolean
  isAdmin: boolean
  menuPages?: MenuLookupPage[]
}

const props = withDefaults(defineProps<Props>(), {
  menuPages: () => [],
})

defineEmits<{
  close: []
}>()

const linkComponent = computed(() => {
  if (props.item.type === 'custom' && props.item.url?.startsWith('http')) {
    return 'a'
  }
  return 'router-link'
})

const computedUrl = computed(() => {
  return resolveMenuItemUrl(props.item, props.menuPages)
})

const displayLabel = computed(() => {
  return resolveMenuItemLabel(props.item, props.menuPages)
})

const customClasses = computed(() => {
  if (!props.item.cssClass) return {}
  return props.item.cssClass.split(' ').reduce((acc, className) => {
    acc[className] = true
    return acc
  }, {} as Record<string, boolean>)
})
</script>

<style scoped>
.dropdown-item {
  display: flex;
  align-items: center;
  padding: 0.625rem 1rem;
  color: var(--nav-text-color, inherit);
  text-decoration: none;
  transition: all 0.2s ease;
  white-space: nowrap;
  border-radius: 4px;
  margin: 0.125rem 0.375rem;
}

.dropdown-item:hover {
  background: var(--nav-dropdown-hover-bg, rgba(255, 255, 255, 0.12));
  color: var(--nav-text-color, inherit);
}
</style>
